import { X, Eye } from 'lucide-react';
import { FormSection } from '../../types/formBuilder';
import { FormPreviewRenderer } from './FormPreviewRenderer';

interface FormPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  sections: FormSection[];
  workflowPath?: string;
  outputKey?: string;
  /** Override header copy (e.g. agent onboarding vs model). */
  previewTitle?: string;
  previewSubtitle?: string;
}

export const FormPreviewModal = ({ 
  isOpen, 
  onClose, 
  sections, 
  workflowPath = 'fal-ai/flux-pro/v1.1',
  outputKey = 'images',
  previewTitle = 'Model Configuration Form',
  previewSubtitle = 'Live preview with form validation and submission testing',
}: FormPreviewModalProps) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-surface z-modal-content flex flex-col">
      <div className="flex flex-col h-full">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-subtle bg-surface">
          <div className="flex items-center space-x-3">
            <Eye className="w-6 h-6 text-accent" />
            <div>
              <h2 className="text-xl font-bold text-th-primary">Live Form Preview</h2>
              <p className="text-xs text-th-secondary">Test your form configuration in real-time</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-th-secondary hover:text-th-primary transition-colors rounded-lg hover:bg-overlay"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto p-6 space-y-8">
            {/* Form Title */}
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-th-primary mb-2">
                {previewTitle}
              </h3>
              <p className="text-th-secondary">
                {previewSubtitle}
              </p>
            </div>

            {/* Form Sections */}
            <FormPreviewRenderer 
              sections={sections}
              workflowPath={workflowPath}
              outputKey={outputKey}
              creditCost={10}
            />
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-between p-4 border-t border-subtle bg-surface">
          <div className="flex items-center space-x-4">
            <div className="text-sm text-th-secondary">
              Live Preview Mode • Test all form interactions
            </div>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-brand text-surface rounded-lg font-medium hover:bg-brand/90 transition-all"
          >
            Close Preview
          </button>
        </div>
      </div>
    </div>
  );
};